/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types */

/*
 * javascript important dependencies (must be locked to these versions that are known to work together):
 *
 * jquery 2.2.4
 * jquery-ui 1.12.1
 * jquery-ui-dist 1.12.1
 * jquery-contextmenu 2.9.0
 * d3 5.16.0
 * bootstrap 4.5.3
 * fontawesome 5.12.1
 *
 */

import {BrowserModule}                from '@angular/platform-browser';

import {
  Injector,
  NgModule, provideExperimentalZonelessChangeDetection
} from '@angular/core';

import {
  HTTP_INTERCEPTORS,
  HttpClientModule
}                                     from '@angular/common/http';

import { environment }                from '../environments/environment';

import {
  FormsModule,
  ReactiveFormsModule
}                                     from '@angular/forms';

import {
  Routes,
  RouterOutlet,
  RouterModule,
  RouteReuseStrategy
}                                     from '@angular/router';

import {AppRouteReuseStrategy}        from './app.route.reuse.strategy';

import {provideAnimationsAsync}       from "@angular/platform-browser/animations/async";

import {AppComponent}                 from './app.component';

import {AppRouting}                   from './app-routing';
import {NavigationBarComponent}       from './components/navigation.bar.component';
import {PanoramaControlsComponent}    from '@/components/panorama.controls.component';
import {PanoramaSelector}             from '@/components/panorama.selector';
import {LeafletControls}              from './components/leaflet/leaflet.controls';
import {PanoramaBrightnessComponent}  from '@/components/panorama.brightness.component';
import {PanoramaNavigator}            from './components/panorama.navigator';
import {PanoramaContrastComponent}    from '@/components/panorama.contrast.component';
import {PanoramaComponent}            from './screens/panorama/panorama.component';
import {KRPanoramaViewer}             from './components/krpano/k.r.panorama.viewer';
import {LeafletViewer}                from './components/leaflet/leaflet.viewer';
import {PanoramaController}           from './components/panorama.controller';
import {OverwatchController}          from './components/overwatch.controller';
import {MyProjectsComponent}          from '@/screens/my-projects/my.projects.component';
import {MyOrganizationsComponent}     from './screens/my-organizations/my.organizations.component';
import {ProjectDetailsComponent}      from './screens/project/project.details.component';
import {OrganizationDetailsComponent} from './screens/organization/organization.details.component';
import {HelpComponent}                from '@/screens/help/help.component';
import {OverwatchComponent}           from '@/screens/overwatch/overwatch.component';
import {LoginComponent}               from './screens/login/login.component';
import {LogoutComponent}              from './components/logout.component';
import {RegisterComponent}            from './screens/register/register.component';
import {PrivacyComponent}             from './screens/privacy/privacy.component';
import {AuthHeaderStamp}              from './helpers/auth.header.stamp';
import {AlertComponent}               from './components/alert.component';
import {ProfileComponent}             from './screens/profile/profile.component';
import {UserProfileComponent}         from './screens/user/user.profile.component';
import {AdminComponent}               from './screens/administration/admin.component';
import {AdminSidebar}                 from '@/components/admin.sidebar';
import {SubdivisionsListComponent}    from './screens/administration/subdivisions.list.component';
import {OrganizationsListComponent}   from './screens/administration/organizations.list.component';
import {SubdivisionListComponent}     from '@/components/subdivision.list.component';
import {ProjectListComponent}         from '@/components/project.list.component';
import {SyncJobsComponent}            from './screens/administration/sync.jobs.component';
import {UsersListComponent}           from '@/components/users.list.component';
import {OrganizationListComponent}    from '@/components/organization.list.component';
import {ProjectsListComponent}        from './screens/administration/projects.list.component';
import {UserListComponent}            from './screens/administration/user.list.component';
import {UserDetailComponent}          from './screens/administration/user.detail.component';
import {PendingUsersComponent}        from './screens/administration/pending.users.component';
import {ApproveUserComponent}         from './screens/administration/approve.user.component';
import {NewUserComponent}             from './screens/administration/new.user.component';

import {AvatarModule}                 from 'primeng/avatar';
import {KnobModule}                   from 'primeng/knob';
import {TableModule}                  from 'primeng/table';
import {ButtonModule}                 from 'primeng/button';
import {TagModule}                    from 'primeng/tag';
import {PasswordModule}               from 'primeng/password';
import {DividerModule}                from 'primeng/divider';
import {InputNumberModule}            from 'primeng/inputnumber';
import {DropdownModule}               from 'primeng/dropdown';
import {InputTextModule}              from 'primeng/inputtext';
import {SplitterModule}               from 'primeng/splitter';
import {BlockUIModule}                from 'primeng/blockui';
import {TooltipModule}                from 'primeng/tooltip';
import {RippleModule}                 from 'primeng/ripple';
import {KeyFilterModule}              from 'primeng/keyfilter';
import {ContextMenuModule}            from 'primeng/contextmenu';
import {InputSwitchModule}            from 'primeng/inputswitch';
import {InplaceModule}                from 'primeng/inplace';
import {AutoCompleteModule}           from 'primeng/autocomplete';
import {AccordionModule}              from 'primeng/accordion';
import {InputGroupModule}             from "primeng/inputgroup";
import {FloatLabelModule}             from 'primeng/floatlabel';
import {providePrimeNG}               from "primeng/config";
import Aura                           from '@primeng/themes/aura';
import {MatToolbarModule}             from '@angular/material/toolbar';
import {MatSidenavModule}             from '@angular/material/sidenav';
import {MatIconModule}                from '@angular/material/icon';
import {MatListModule}                from '@angular/material/list';
import {MatCardModule}                from '@angular/material/card';
import {MatGridListModule}            from '@angular/material/grid-list';
import {MatTooltipModule}             from '@angular/material/tooltip';
import {MatFormFieldModule}           from '@angular/material/form-field';

import {BrowserAnimationsModule}      from '@angular/platform-browser/animations';

/* bring in the service locator, we have to set this up so we can "see" the Injector */
import {ServiceLocator}               from '@/services/service.locator';
import {Logger, LoggerLevel}          from '@/util/logger';

/* regular Angular module declarations etc. */

@NgModule({

  declarations: [
    NavigationBarComponent,
    PanoramaControlsComponent,
    PanoramaSelector,
    LeafletControls,
    PanoramaBrightnessComponent,
    PanoramaNavigator,
    PanoramaContrastComponent,
    ProjectListComponent,
    SyncJobsComponent,
    UsersListComponent,
    OrganizationListComponent,
    SubdivisionListComponent,
    ProjectDetailsComponent,
    OrganizationDetailsComponent,
    AppComponent,
    PanoramaComponent,
    KRPanoramaViewer,
    LeafletViewer,
    PanoramaController,
    OverwatchController,
    MyProjectsComponent,
    MyOrganizationsComponent,
    HelpComponent,
    OverwatchComponent,
    LoginComponent,
    LogoutComponent,
    RegisterComponent,
    PrivacyComponent,
    AlertComponent,
    ProfileComponent,
    UserProfileComponent,
    AdminComponent,
    UserListComponent,
    SubdivisionsListComponent,
    OrganizationsListComponent,
    ProjectsListComponent,
    UserDetailComponent,
    PendingUsersComponent,
    ApproveUserComponent,
    NewUserComponent,
    AdminSidebar
  ],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRouting,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    RouterOutlet,
    RouterModule,
    KnobModule,
    TableModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    PasswordModule,
    DividerModule,
    InputNumberModule,
    DropdownModule,
    InputTextModule,
    KeyFilterModule,
    ContextMenuModule,
    InputSwitchModule,
    InplaceModule,
    AutoCompleteModule,
    AccordionModule,
    InputGroupModule,
    FloatLabelModule,
    RippleModule,
    SplitterModule,
    BlockUIModule,
    TooltipModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatCardModule,
    MatGridListModule,
    MatTooltipModule,
    MatFormFieldModule,
    RippleModule
  ],

  exports: [
    RouterModule,
  ],

  providers: [
    /* dockerBackendProvider, */
    /* fakeBackendProvider, */
    provideExperimentalZonelessChangeDetection(),
    { provide: HTTP_INTERCEPTORS,  useClass: AuthHeaderStamp, multi: true },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura
      }
    })
  ],

  /* which component is the entry point? */
  bootstrap: [AppComponent],

})

export class AppModule {

  constructor(private injector: Injector) {

    /* tell the logger what verbosity we are at */

    if (environment.production) {

      Logger.setVerbosity(LoggerLevel.WARN);

      Logger.warning(`[AppModule] logging at WARNING and above.`);

    } else {

      Logger.warning(`[AppModule] logging ALL.`);
    }

    /* tell the service locator what injector to use */

    ServiceLocator.injector = injector;
  }

}
